/**
 * Header and footer for the signed-out site: home, feature pages, pricing and
 * the legal pages. The Features menu is built from `FEATURES`, so a new feature
 * page appears here without touching this file.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { FEATURES } from '@/content/features';
import { cn } from '@/utils/cn';
import googlePlayBadge from '@/assets/google-play-badge.svg';

const PLAY_URL = 'https://play.google.com/store/apps/details?id=com.factree.hhapp';

function Brand({ dark }: { dark?: boolean }) {
  return (
    <Link to="/" className="flex shrink-0 items-center gap-2" aria-label="FactreeTech home">
      <img src="/logo.png" alt="" width={32} height={32} className="h-8 w-8 rounded-lg" />
      <span className={cn('text-xl font-bold', dark ? 'text-white' : 'text-neutral-900')}>FactreeTech</span>
    </Link>
  );
}

function FeaturesMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900"
      >
        Features
        <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="absolute left-1/2 top-full z-50 mt-2 w-[44rem] max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-2xl border border-neutral-200 bg-white p-3 shadow-large">
          <div className="grid grid-cols-2 gap-1">
            {FEATURES.map((f) => (
              <Link
                key={f.slug}
                to={`/features/${f.slug}`}
                onClick={() => setOpen(false)}
                className="flex items-start gap-3 rounded-xl p-3 hover:bg-neutral-50"
              >
                <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', f.tone)}>
                  <f.icon className="h-5 w-5" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-neutral-900">{f.name}</span>
                  <span className="block text-xs leading-snug text-neutral-600">{f.summary}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function PublicHeader() {
  const { isAuthenticated } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setMenuOpen(false), [pathname]);

  const navClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'rounded-lg px-3 py-2 text-sm font-medium hover:bg-neutral-100 hover:text-neutral-900',
      isActive ? 'text-primary-700' : 'text-neutral-700'
    );

  return (
    <header className="sticky top-0 z-40 border-b border-neutral-200/60 bg-white/90 backdrop-blur-sm">
      <div className="mx-auto max-w-7xl px-4 pt-[env(safe-area-inset-top)] sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4 py-4">
          <Brand />

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
            <FeaturesMenu />
            <NavLink to="/pricing" className={navClass}>Pricing</NavLink>
          </nav>

          <div className="hidden items-center gap-2 lg:flex">
            {isAuthenticated ? (
              <Link to="/dashboard" className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700">
                Go to dashboard
              </Link>
            ) : (
              <>
                <Link to="/login" className="rounded-lg px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-100">
                  Sign in
                </Link>
                <Link to="/register" className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700">
                  Get started free
                </Link>
              </>
            )}
          </div>

          <button
            type="button"
            className="rounded-lg p-2 text-neutral-700 hover:bg-neutral-100 lg:hidden"
            onClick={() => setMenuOpen((o) => !o)}
            aria-expanded={menuOpen}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {menuOpen && (
          <div className="max-h-[calc(100vh-5rem)] overflow-y-auto border-t border-neutral-200 pb-4 lg:hidden">
            <p className="px-1 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-neutral-500">Features</p>
            <div className="grid grid-cols-1 gap-0.5 sm:grid-cols-2">
              {FEATURES.map((f) => (
                <Link key={f.slug} to={`/features/${f.slug}`} className="flex items-center gap-3 rounded-lg px-1 py-2 hover:bg-neutral-50">
                  <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', f.tone)}>
                    <f.icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 text-sm font-medium text-neutral-900">{f.name}</span>
                </Link>
              ))}
            </div>
            <Link to="/pricing" className="mt-2 block rounded-lg px-1 py-2 text-sm font-semibold text-neutral-900 hover:bg-neutral-50">
              Pricing
            </Link>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              {isAuthenticated ? (
                <Link to="/dashboard" className="rounded-lg bg-primary-600 px-4 py-2.5 text-center text-sm font-semibold text-white">
                  Go to dashboard
                </Link>
              ) : (
                <>
                  <Link to="/register" className="rounded-lg bg-primary-600 px-4 py-2.5 text-center text-sm font-semibold text-white">
                    Get started free
                  </Link>
                  <Link to="/login" className="rounded-lg border border-neutral-300 px-4 py-2.5 text-center text-sm font-medium text-neutral-800">
                    Sign in
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

export function PublicFooter() {
  return (
    <footer className="bg-neutral-900 text-neutral-400">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-[1.2fr_2fr_1fr]">
          <div className="space-y-4">
            <Brand dark />
            <p className="max-w-xs text-sm leading-relaxed">
              Farm management for Australian orchards, vineyards and nurseries: one account, from the paddock to the pay run.
            </p>
            <a href={PLAY_URL} target="_blank" rel="noopener noreferrer" aria-label="Get FactreeTech on Google Play" className="inline-block">
              <img src={googlePlayBadge} alt="Get it on Google Play" className="h-[48px] w-auto" />
            </a>
          </div>
          <div>
            <p className="mb-3 text-sm font-semibold text-white">Features</p>
            <ul className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              {FEATURES.map((f) => (
                <li key={f.slug}>
                  <Link to={`/features/${f.slug}`} className="hover:text-neutral-100">{f.name}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="mb-3 text-sm font-semibold text-white">FactreeTech</p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/pricing" className="hover:text-neutral-100">Pricing</Link></li>
              <li><Link to="/register" className="hover:text-neutral-100">Create an account</Link></li>
              <li><Link to="/login" className="hover:text-neutral-100">Sign in</Link></li>
              <li><Link to="/terms" className="hover:text-neutral-100">Terms and Conditions</Link></li>
              <li><Link to="/privacy" className="hover:text-neutral-100">Privacy Policy</Link></li>
            </ul>
          </div>
        </div>
        <p className="mt-10 border-t border-neutral-800 pt-6 text-xs text-neutral-500">
          Prices are in Australian dollars and include GST.
        </p>
      </div>
    </footer>
  );
}

export default function PublicLayout({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-white">
      <PublicHeader />
      <main className="flex-1">{children}</main>
      <PublicFooter />
    </div>
  );
}
